/**
 * Rental ROI Calculation Engine
 */

// ── Mortgage helpers ──────────────────────────────────────────────────────────

/** D30: Standard amortising monthly payment. Zero-rate handled as flat paydown. */
export function calcMonthlyPayment(loanAmount, annualRate, years) {
  if (loanAmount <= 0) return 0
  if (annualRate === 0) return loanAmount / (years * 12)
  const r = annualRate / 12
  const n = years * 12
  return loanAmount * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
}

/**
 * L column: Remaining balance after `yearNum` full years.
 * Formula: D26*(1+D28/12)^(year*12) - D30*((1+D28/12)^(year*12)-1)/(D28/12)
 * Returns 0 once paid off.
 */
export function calcRemainingBalance(loanAmount, annualRate, years, yearNum) {
  if (loanAmount <= 0 || yearNum <= 0) return loanAmount
  const monthsPaid = yearNum * 12
  if (annualRate === 0) {
    return Math.max(0, loanAmount - (loanAmount / (years * 12)) * monthsPaid)
  }
  const r = annualRate / 12
  const payment = calcMonthlyPayment(loanAmount, annualRate, years)
  const balance = loanAmount * Math.pow(1 + r, monthsPaid) -
                  payment * (Math.pow(1 + r, monthsPaid) - 1) / r
  return Math.max(0, balance)
}

// ── Main analysis ─────────────────────────────────────────────────────────────

/**
 * Compute all values matching the workbook's output cells exactly.
 * All returned expense values are POSITIVE (absolute).
 */
export function computeROI(inputs) {
  const {
    listingPrice       = 130000, // B13
    improvements       = 0,      // B14
    downPaymentPct     = 0.20,   // B15
    interestRate       = 0.06,   // B16
    mortgageYears      = 30,     // B17
    rentMonthly        = 1300,   // B18
    otherRevMonthly    = 0,      // B19
    vacancyRate        = 0.05,   // D13
    revenueGrowthPct   = 0.03,   // D14 / B32
    expenseGrowthPct   = 0.02,   // D15 / B33
    appreciationPct    = 0.03,   // D16 / B34
    propertyTaxPct     = 0.02,   // D17
    propertyTaxFlat    = 0,      // flat $ amount (used when propTaxMode === 'flat')
    propTaxMode        = 'pct',  // 'pct' | 'flat'
    managementFeePct   = 0.08,   // D18
    maintenanceResPct  = 0.10,   // D19
    insuranceYearly    = 1000,   // E13
    advertisingYearly  = 0,      // E14
    utilitiesMonthly   = 0,      // E15
    hoaYearly          = 0,      // E16
    miscYearly         = 0,      // E17
    yearBuilt          = 1994,   // E18
    unitCount          = 1,      // E19
  } = inputs

  // ── B column: Cost assumptions ────────────────────────────────────────────
  const salesPrice    = listingPrice                          // B25
  const closingCosts  = salesPrice * 0.02                    // B27 (fixed 2%)
  const downPayment   = salesPrice * downPaymentPct          // B26
  const totalCost     = salesPrice + closingCosts + improvements // B29
  const cashOutlay    = downPayment + closingCosts + improvements // B30
  const loanAmount    = salesPrice - downPayment             // D26 = C26

  // ── Mortgage ──────────────────────────────────────────────────────────────
  const monthlyPayment = calcMonthlyPayment(loanAmount, interestRate, mortgageYears) // D30
  const annualMortgage = monthlyPayment * 12                 // D59 * 12

  // ── Year-1 Revenue (C/D columns) ─────────────────────────────────────────
  // C38 / F26 — gross rental income per month (before vacancy)
  const grossRentMonthly     = rentMonthly
  // C39 — vacancy loss is applied only to rental income
  const vacancyLossMonthly   = -grossRentMonthly * vacancyRate
  // C40
  const netRentalMonthly     = grossRentMonthly + vacancyLossMonthly
  // C41
  const otherIncomeMonthly   = otherRevMonthly
  // C42 — gross income (net rent + other)
  const grossIncomeMonthly   = netRentalMonthly + otherIncomeMonthly

  // Annual equivalents
  const grossRentYearly      = grossRentMonthly * 12         // D38
  const vacancyLossYearly    = vacancyLossMonthly * 12       // D39
  const netRentalYearly      = netRentalMonthly * 12         // D40
  const otherIncomeYearly    = otherIncomeMonthly * 12       // D41
  const grossIncomeYearly    = grossIncomeMonthly * 12       // D42

  // ── Year-1 Expenses ───────────────────────────────────────────────────────
  // NOTE: Management & Maintenance are % of GROSS RENT (before vacancy) — D47/D48
  const expTaxes       = propTaxMode === 'flat' ? -propertyTaxFlat : -(salesPrice * propertyTaxPct) // D45
  const expInsurance   = -insuranceYearly                            // D46
  const expMgmt        = -(managementFeePct * grossRentYearly)       // D47 = -D18*C38*12
  const expMaint       = -(maintenanceResPct * grossRentYearly)      // D48 = -D38*D19
  const expAdvertising = -advertisingYearly                          // D49
  const expUtilities   = -(utilitiesMonthly * 12)                    // D50
  const expHoa         = -hoaYearly                                  // D51
  const expMisc        = -miscYearly                                 // D52
  const totalExpenses  = expTaxes + expInsurance + expMgmt + expMaint +
                         expAdvertising + expUtilities + expHoa + expMisc // D53

  // ── NOI & cash flow ───────────────────────────────────────────────────────
  const noi            = grossIncomeYearly + totalExpenses    // D55
  const annualCashFlow = noi - annualMortgage                 // D60
  const monthlyCashFlow= annualCashFlow / 12                  // C60

  // ── KPIs (F column) ───────────────────────────────────────────────────────
  const capRate         = salesPrice > 0 ? noi / salesPrice : 0  // F3
  const cocROI          = cashOutlay > 0 ? annualCashFlow / cashOutlay : 0 // F4
  const costPerUnit     = unitCount > 0 ? totalCost / unitCount : totalCost // F5
  const monthlyPerUnit  = unitCount > 0 ? monthlyCashFlow / unitCount : monthlyCashFlow // F6
  const grm             = (rentMonthly * 12) > 0 ? salesPrice / (rentMonthly * 12) : 0 // F7

  // ── Year-1 return analysis (D62–D68) ─────────────────────────────────────
  const balance_yr1     = calcRemainingBalance(loanAmount, interestRate, mortgageYears, 1) // D63
  const equity_yr1      = loanAmount - balance_yr1                              // D64
  const propValue_yr1   = salesPrice * (1 + appreciationPct)                   // D65
  const appreciation_yr1= propValue_yr1 - salesPrice                            // D66
  const totalReturn_yr1 = annualCashFlow + equity_yr1 + appreciation_yr1       // D67
  const totalROI_yr1    = cashOutlay > 0 ? totalReturn_yr1 / cashOutlay : 0    // D68 / F9

  // ── 3-Year Pro Forma (D/E/F columns — detailed per-expense growth) ────────
  const proForma = [1, 2, 3].map((yr) => {
    // Revenue grows at revenueGrowthPct each year from Year 1
    const rG = Math.pow(1 + revenueGrowthPct, yr - 1)
    const eG = Math.pow(1 + expenseGrowthPct, yr - 1)
    const aG = Math.pow(1 + appreciationPct,  yr - 1)

    const rent_y        = grossRentYearly * rG
    const vacancy_y     = vacancyLossYearly * rG          // vacancy grows with rent
    const netRent_y     = rent_y + vacancy_y
    const other_y       = otherIncomeYearly * rG
    const grossIncome_y = netRent_y + other_y

    // Each expense category grows at its specific rate
    const expenses_y = {
      taxes:       expTaxes * aG,          // grows at appreciation
      insurance:   expInsurance * eG,      // expense growth
      mgmt:        expMgmt * rG,           // revenue growth (% of rent)
      maint:       expMaint * eG,          // expense growth
      advertising: expAdvertising * eG,
      utilities:   expUtilities * eG,
      hoa:         expHoa * eG,
      misc:        expMisc * eG,
    }
    const totalExp_y = Object.values(expenses_y).reduce((a, b) => a + b, 0)

    const noi_y         = grossIncome_y + totalExp_y
    const cashFlow_y    = noi_y - annualMortgage

    const prevBal       = calcRemainingBalance(loanAmount, interestRate, mortgageYears, yr - 1)
    const bal_y         = calcRemainingBalance(loanAmount, interestRate, mortgageYears, yr)
    const equity_y      = prevBal - bal_y
    const propVal_y     = salesPrice * Math.pow(1 + appreciationPct, yr)
    const prevVal       = salesPrice * Math.pow(1 + appreciationPct, yr - 1)
    const apprec_y      = propVal_y - prevVal
    const totalRet_y    = cashFlow_y + equity_y + apprec_y
    const totalROI_y    = cashOutlay > 0 ? totalRet_y / cashOutlay : 0

    // Cash ROI (D61 equivalent)
    const cashROI_y     = cashOutlay > 0 ? cashFlow_y / cashOutlay : 0

    return {
      year: yr,
      grossRent:      rent_y,
      vacancyLoss:    Math.abs(vacancy_y),
      netRental:      netRent_y,
      otherIncome:    other_y,
      grossIncome:    grossIncome_y,
      // expenses (absolute)
      expTaxes:       Math.abs(expenses_y.taxes),
      expInsurance:   Math.abs(expenses_y.insurance),
      expMgmt:        Math.abs(expenses_y.mgmt),
      expMaint:       Math.abs(expenses_y.maint),
      expAdvertising: Math.abs(expenses_y.advertising),
      expUtilities:   Math.abs(expenses_y.utilities),
      expHoa:         Math.abs(expenses_y.hoa),
      expMisc:        Math.abs(expenses_y.misc),
      totalExpenses:  Math.abs(totalExp_y),
      noi:            noi_y,
      annualCashFlow: cashFlow_y,
      cashROI:        cashROI_y,
      remainingBalance: bal_y,
      equityAccrued:  equity_y,
      propertyValue:  propVal_y,
      appreciation:   apprec_y,
      totalReturn:    totalRet_y,
      totalROI:       totalROI_y,
    }
  })

  // ── 30-year return vs time (G–Q columns: simplified uniform growth) ────────
  // Workbook: H (gross income) grows at D14, I (costs) grows at D15
  const longTerm = []
  let prevBalance = loanAmount
  let prevValue   = salesPrice

  for (let yr = 1; yr <= 40; yr++) {
    const rG = Math.pow(1 + revenueGrowthPct, yr - 1)
    const eG = Math.pow(1 + expenseGrowthPct, yr - 1)

    const gi_y  = grossIncomeYearly * rG                         // H column
    const exp_y = totalExpenses * eG                             // I column (negative)
    const noi_y = gi_y + exp_y                                   // J column
    const debt_y = yr <= mortgageYears ? -annualMortgage : 0     // K column
    const cf_y  = noi_y + debt_y                                 // R column

    const bal_y = calcRemainingBalance(loanAmount, interestRate, mortgageYears, yr) // L
    const eq_y  = prevBalance - bal_y                            // M
    const val_y = salesPrice * Math.pow(1 + appreciationPct, yr) // N
    const apr_y = val_y - prevValue                              // O

    const totalRet_y = cf_y + eq_y + apr_y                      // P
    const totalROI_y = cashOutlay > 0 ? totalRet_y / cashOutlay : 0 // Q
    const cocROI_y   = cashOutlay > 0 ? cf_y / cashOutlay : 0   // S

    longTerm.push({
      year: yr,
      grossIncome:    gi_y,
      totalExpenses:  Math.abs(exp_y),
      noi:            noi_y,
      debtService:    Math.abs(debt_y),
      annualCashFlow: cf_y,
      remainingBalance: bal_y,
      equityAccrued:  eq_y,
      propertyValue:  val_y,
      appreciation:   apr_y,
      totalReturn:    totalRet_y,
      totalROI:       totalROI_y,
      cocROI:         cocROI_y,
    })

    prevBalance = bal_y
    prevValue   = val_y
  }

  // ── COC ROI vs Down Payment sensitivity (A113–F134) ───────────────────────
  const cocSensitivity = []
  for (let pct = 10; pct <= 30; pct++) {
    const dp     = salesPrice * (pct / 100)
    const loan_s = salesPrice - dp
    const pay_s  = calcMonthlyPayment(loan_s, interestRate, mortgageYears)
    const outlay_s = dp + closingCosts + improvements
    const annCF_s  = noi - pay_s * 12  // NOI stays same, only mortgage changes
    const coc_s    = outlay_s > 0 ? annCF_s / outlay_s : 0
    cocSensitivity.push({ pct, downPayment: dp, mortgage: pay_s, annualCashFlow: annCF_s, cocROI: coc_s })
  }

  return {
    // Acquisition stack
    salesPrice, closingCosts, improvements, totalCost, downPayment, cashOutlay, loanAmount,
    // Mortgage
    monthlyPayment, annualMortgage,
    // Year-1 income
    grossRentMonthly, vacancyLossMonthly: Math.abs(vacancyLossMonthly),
    netRentalMonthly, grossIncomeMonthly,
    grossRentYearly, vacancyLossYearly: Math.abs(vacancyLossYearly),
    netRentalYearly, grossIncomeYearly,
    // Year-1 expenses (positive)
    expTaxes: Math.abs(expTaxes),
    expInsurance: Math.abs(expInsurance),
    expMgmt: Math.abs(expMgmt),
    expMaint: Math.abs(expMaint),
    expAdvertising: Math.abs(expAdvertising),
    expUtilities: Math.abs(expUtilities),
    expHoa: Math.abs(expHoa),
    expMisc: Math.abs(expMisc),
    totalExpenses: Math.abs(totalExpenses),
    // NOI & cash flow
    noi, annualCashFlow, monthlyCashFlow,
    // KPIs
    capRate, cocROI, costPerUnit, monthlyPerUnit, grm,
    // Year-1 return
    balance_yr1, equity_yr1, propValue_yr1, appreciation_yr1, totalReturn_yr1, totalROI_yr1,
    // Tables
    proForma,
    longTerm,
    cocSensitivity,
  }
}

/** Default inputs matching the workbook's sample values */
export const DEFAULT_ROI_INPUTS = {
  listingPrice:      130000,
  improvements:      0,
  downPaymentPct:    0.20,
  interestRate:      0.06,
  mortgageYears:     30,
  rentMonthly:       1300,
  otherRevMonthly:   0,
  vacancyRate:       0.05,
  revenueGrowthPct:  0.03,
  expenseGrowthPct:  0.02,
  appreciationPct:   0.03,
  propertyTaxPct:    0.02,
  propertyTaxFlat:   0,
  managementFeePct:  0.08,
  maintenanceResPct: 0.10,
  insuranceYearly:   0,
  advertisingYearly: 0,
  utilitiesMonthly:  0,
  hoaYearly:         0,
  miscYearly:        0,
  yearBuilt:         1994,
  unitCount:         1,
}
